import type { RateioMapaColuna, RateioMapaLinha, RateioResponse } from '@triade/contracts';
import { CheckCircle2 } from 'lucide-react';
import { ehZero, formatarMoeda } from '@/lib/format';
import { NUMERO, TD_VALOR, TH_VALOR } from './comum';

/** Largura minima da coluna de origem (rotulo da linha). */
const LARG_ORIGEM = 200;

/**
 * Aba Mapa de rateio: duas matrizes (custos indiretos e absorcao), linhas x centros produtivos. As colunas vem de `colunasMapa`
 * (apoio primeiro, depois producao; sem administrativo). Os valores vem prontos da API; a tela so exibe. Sem R$ (PCP) mostra so
 * os rotulos das linhas.
 */
export function AbaMapa({ rateio }: { rateio: RateioResponse }) {
  const comValores = [...rateio.mapaCif, ...rateio.mapaAbsorcao].some((l) => l.celulas !== undefined);
  const absorcao: RateioMapaLinha[] = rateio.mapaAbsorcao.map((l) => ({
    ...l,
    rotulo: l.ordem !== undefined ? `${l.ordem}. ${l.rotulo}` : l.rotulo,
  }));

  return (
    <div className="flex flex-col gap-6">
      {!comValores && (
        <p role="status" className="rounded-xl border border-border bg-surface-muted px-4 py-3 text-small text-ink-secondary">
          Valores ocultos para o seu acesso.
        </p>
      )}
      <Matriz
        titulo="Custos indiretos"
        linhas={rateio.mapaCif}
        colunas={rateio.colunasMapa}
        comValores={comValores}
        rotuloColuna="Origem"
        comForma
        rodape={rateio.rodapeMapaCif}
      />
      <Matriz
        titulo="Rateio por absorção"
        linhas={absorcao}
        colunas={rateio.colunasMapa}
        comValores={comValores}
        rotuloColuna="Centro de apoio"
        rodape={rateio.rodapeMapaAbsorcao}
      />
      {rateio.conferencia && <Conferencia conferencia={rateio.conferencia} />}
    </div>
  );
}

type ConferenciaResponse = NonNullable<RateioResponse['conferencia']>;

/** Conferencia dos mapas (calculada na API): so exibe. Sem `conferencia` (PCP) nao renderiza. */
function Conferencia({ conferencia }: { conferencia: ConferenciaResponse }) {
  const { mapaCustosIndiretos, diferenca, ok, motivos } = conferencia;

  if (ok && motivos.length === 0) {
    return (
      <div role="status" data-testid="conferencia-ok" className="flex items-center gap-2 rounded-xl bg-success-bg px-4 py-3 text-small text-success-ink">
        <CheckCircle2 aria-hidden className="h-4 w-4 shrink-0 text-success-ink" />
        <span>Mapa de custos indiretos = Rateio por absorção = {formatarMoeda(mapaCustosIndiretos)}</span>
      </div>
    );
  }

  return (
    <div data-testid="conferencia-diferenca" className="flex flex-col gap-2 rounded-xl bg-danger-bg px-4 py-3 text-small text-danger-ink">
      <p className="font-semibold">Diferença de {formatarMoeda(diferenca)}</p>
      {motivos.length > 0 && (
        <ul className="flex flex-col gap-1">
          {motivos.map((m, i) => (
            <li key={`${m.codigo}-${i}`} className="flex flex-wrap justify-between gap-x-4">
              <span>{m.mensagem}</span>
              <span className="tabular font-semibold whitespace-nowrap">{formatarMoeda(m.valor)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Colunas de centro na ordem da tela: apoio primeiro, depois producao (o cabecalho agrupado usa a mesma ordem). */
function ordenarColunas(colunas: RateioMapaColuna[]): { apoio: RateioMapaColuna[]; producao: RateioMapaColuna[]; todas: RateioMapaColuna[] } {
  const apoio = colunas.filter((c) => c.grupo === 'APOIO');
  const producao = colunas.filter((c) => c.grupo === 'PRODUCAO');
  return { apoio, producao, todas: [...apoio, ...producao] };
}

function Matriz({
  titulo,
  linhas,
  colunas,
  comValores,
  rotuloColuna,
  comForma = false,
  rodape,
}: {
  titulo: string;
  linhas: RateioMapaLinha[];
  colunas: RateioMapaColuna[];
  comValores: boolean;
  rotuloColuna: string;
  /** Mapa de custos indiretos: coluna "Forma de rateio" (texto que a API manda em cada linha). */
  comForma?: boolean;
  /** Totais por coluna, prontos da API (so com custo.ver). Ausente = sem rodape. */
  rodape?: { celulas: Record<string, string>; total: string };
}) {
  if (linhas.length === 0) {
    return (
      <section aria-label={titulo} className="flex flex-col gap-2">
        <h3 className="text-small font-semibold">{titulo}</h3>
        <p role="status" className="px-3 py-4 text-small text-ink-muted">Nenhuma linha nesta matriz.</p>
      </section>
    );
  }

  const { apoio, producao, todas } = ordenarColunas(colunas);
  // "Não alocado" so aparece se alguma linha desta matriz tem valor diferente de zero na chave ''.
  const comNaoAlocado = comValores && linhas.some((l) => l.celulas?.[''] !== undefined && !ehZero(l.celulas['']));

  return (
    <section aria-label={titulo} className="flex flex-col gap-2">
      <h3 className="text-small font-semibold">{titulo}</h3>
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="min-w-full border-collapse text-small" aria-label={`Matriz: ${titulo}`}>
          <thead>
            <tr className="bg-surface-muted text-caption text-ink-muted">
              <th scope="col" rowSpan={2} className={`${TH_VALOR} sticky left-0 z-10 bg-surface-muted text-left align-bottom`} style={{ minWidth: LARG_ORIGEM }}>
                {rotuloColuna}
              </th>
              {comForma && (
                <th scope="col" rowSpan={2} className={`${TH_VALOR} text-left align-bottom`}>
                  Forma de rateio
                </th>
              )}
              {apoio.length > 0 && (
                <th scope="col" colSpan={apoio.length} className={`${TH_VALOR} border-l border-border text-center`}>
                  Centros de custos de apoio
                </th>
              )}
              {producao.length > 0 && (
                <th scope="col" colSpan={producao.length} className={`${TH_VALOR} border-l border-border text-center`}>
                  Centros de custos de produção
                </th>
              )}
              {comNaoAlocado && (
                <th scope="col" rowSpan={2} className={`${TH_VALOR} text-right align-bottom`}>
                  Não alocado
                </th>
              )}
              {comValores && (
                <th scope="col" rowSpan={2} className={`${TH_VALOR} sticky right-0 z-10 bg-surface-muted text-right align-bottom`}>
                  Total
                </th>
              )}
            </tr>
            <tr className="bg-surface-muted text-caption text-ink-muted">
              {todas.map((c) => (
                <th key={c.id} scope="col" className={`${TH_VALOR} text-right`}>
                  <span title={c.nome}>
                    <span className="tabular">{c.codigo}</span> {c.nome}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.chave} className="border-t border-line-soft">
                <td className={`${TD_VALOR} sticky left-0 z-[1] bg-card font-medium`} style={{ minWidth: LARG_ORIGEM }}>
                  {l.rotulo}
                </td>
                {comForma && <td className={`${TD_VALOR} whitespace-nowrap text-ink-secondary`}>{l.forma ?? ''}</td>}
                {todas.map((c) => (
                  <td key={c.id} className={`${TD_VALOR} ${NUMERO}`}>
                    {comValores ? celula(l, c.id) : ''}
                  </td>
                ))}
                {comNaoAlocado && <td className={`${TD_VALOR} ${NUMERO}`}>{comValores ? celula(l, '') : ''}</td>}
                {comValores && <td className={`${TD_VALOR} ${NUMERO} sticky right-0 z-[1] bg-card font-semibold`}>{formatarMoeda(l.total)}</td>}
              </tr>
            ))}
          </tbody>
          {comValores && rodape && (
            <tfoot>
              <tr className="border-t border-border bg-surface-muted font-semibold">
                <td className={`${TD_VALOR} sticky left-0 z-[1] bg-surface-muted`} style={{ minWidth: LARG_ORIGEM }}>
                  Totais
                </td>
                {comForma && <td className={TD_VALOR} />}
                {todas.map((c) => (
                  <td key={c.id} className={`${TD_VALOR} ${NUMERO}`}>
                    {celulaDoRodape(rodape, c.id)}
                  </td>
                ))}
                {comNaoAlocado && <td className={`${TD_VALOR} ${NUMERO}`}>{celulaDoRodape(rodape, '')}</td>}
                <td className={`${TD_VALOR} ${NUMERO} sticky right-0 z-[1] bg-surface-muted`}>{formatarMoeda(rodape.total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </section>
  );
}

/** Total de coluna vindo da API; ausente = vazio. */
function celulaDoRodape(rodape: { celulas: Record<string, string> }, centroId: string): string {
  const valor = rodape.celulas[centroId];
  return valor === undefined ? '' : formatarMoeda(valor);
}

/** Valor da celula vindo da API; ausente = vazio (nao ha conta aqui). */
function celula(linha: RateioMapaLinha, centroId: string): string {
  const valor = linha.celulas?.[centroId];
  return valor === undefined ? '' : formatarMoeda(valor);
}
